import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Category } from '../../models/category.model';
import { CategoryService } from '../../osm-services/category.service';
import { AuthService } from '../../services/auth.service';
import { UserFavAppService } from '../../osm-services/user-fav-app.service';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

interface CategoryWithApps {
  categoryId: string;
  name: string;
  icon: string;
  iconPath: string;
  active: boolean;
  order: number;
  apps: AppInCategory[];
  favoriteCount?: number;  // Added for showing favorite count
}

interface AppInCategory {
  appliId: string;
  name: string;
  icon: string;
  androidImageUrl: string;
  active: boolean;
  order: number;
  category: string;
  subCategory: string;
  link: string;
  fallbackUrl: string;
  fallbackUrl2: string;
  packageName: string;
  openByPackageName: boolean;
  default: boolean;
}

@Component({
  selector: 'ozm-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {
  categories: CategoryWithApps[] = [];
  selectedCategory: CategoryWithApps | null = null;
  activeSpecialLink: string | null = null;  // Track which special link is active
  isLoading = false;
  error: string | null = null;
  isLoggedIn = false;
  userFavoriteApps: any[] = [];
  totalFavoritesCount: number = 0;  // Track total favorites count
  private destroy$ = new Subject<void>();

  @Output() categorySelected = new EventEmitter<CategoryWithApps>();

  constructor(
    private categoryService: CategoryService,
    public userFavAppService: UserFavAppService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Subscribe to user favorites updates FIRST - this is the key subscription that updates counts
    this.userFavAppService.userFavApps$
      .pipe(takeUntil(this.destroy$))
      .subscribe(apps => {
        console.log('Sidebar received favorites update:', apps.length, 'apps');
        this.userFavoriteApps = apps;
        this.totalFavoritesCount = apps.length;  // Update total count
        // Update counts whenever favorites change (even if categories not loaded yet)
        if (this.categories.length > 0) {
          this.updateCategoryFavoriteCounts();
        }
      });
    
    // Subscribe to login state changes to update app count after login
    this.authService.isLoggedIn$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoggedIn => {
        console.log('Sidebar login state changed:', isLoggedIn, 'was:', this.isLoggedIn);
        if (isLoggedIn && !this.isLoggedIn) {
          // Reload favorites when user logs in
          this.isLoggedIn = isLoggedIn;
          this.userFavAppService.loadUserFavApps();
        } else if (!isLoggedIn && this.isLoggedIn) {
          // Clear favorites when user logs out
          this.isLoggedIn = isLoggedIn;
          this.userFavoriteApps = [];
          this.totalFavoritesCount = 0;  // Reset count on logout
          this.updateCategoryFavoriteCounts();
        }
        this.isLoggedIn = isLoggedIn;
      });
    
    // Listen to router events to set active state based on current route
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects || event.url;
        
        // Set active state based on current route
        if (url.startsWith('/my-favorites')) {
          this.activeSpecialLink = 'favorites';
          this.selectedCategory = null;
        } else if (url.startsWith('/cashback-request')) {
          this.activeSpecialLink = 'cashback';
          this.selectedCategory = null;
        } else if (url.startsWith('/category-detail/')) {
          // On category detail page, extract categoryId and set selected category
          this.activeSpecialLink = null;
          const categoryId = url.split('/category-detail/')[1]?.split('?')[0];
          if (categoryId && this.categories.length > 0) {
            this.selectedCategory = this.categories.find(cat => cat.categoryId === categoryId) || null;
          }
        } else if (url.startsWith('/home')) {
          // On home page, clear both
          this.activeSpecialLink = null;
          this.selectedCategory = null;
        } else {
          // Any other route, clear both
          this.activeSpecialLink = null;
          this.selectedCategory = null;
        }
      });
    
    // Check initial auth state and load favorites BEFORE loading categories
    this.checkAuthState().then(() => {
      // Load categories after auth check completes
      this.loadCategories();
      
      // Set initial active state based on current route after a delay to ensure categories are loaded
      setTimeout(() => {
        this.setActiveStateFromRoute(this.router.url);
      }, 300);
    });
  }

  private setActiveStateFromRoute(url: string): void {
    if (url.startsWith('/my-favorites')) {
      this.activeSpecialLink = 'favorites';
      this.selectedCategory = null;
    } else if (url.startsWith('/cashback-request')) {
      this.activeSpecialLink = 'cashback';
      this.selectedCategory = null;
    } else if (url.startsWith('/category-detail/')) {
      this.activeSpecialLink = null;
      const categoryId = url.split('/category-detail/')[1]?.split('?')[0];
      if (categoryId && this.categories.length > 0) {
        this.selectedCategory = this.categories.find(cat => cat.categoryId === categoryId) || null;
      }
    } else {
      this.activeSpecialLink = null;
      this.selectedCategory = null;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async checkAuthState(): Promise<void> {
    try {
      const user = await this.authService.getUser();
      const isLoggedIn = !!user;
      console.log('checkAuthState: user logged in:', isLoggedIn);
      
      // Load user favorites if logged in (force load on initial check)
      if (isLoggedIn) {
        this.isLoggedIn = true;
        // Always load favorites on initial auth check - this will trigger the userFavApps$ subscription
        // Wait a bit for the subscription to be ready
        await new Promise(resolve => setTimeout(resolve, 100));
        this.userFavAppService.loadUserFavApps();
      }
    } catch (error) {
      console.error('checkAuthState error:', error);
      this.isLoggedIn = false;
    }
  }

  private updateCategoryFavoriteCounts(): void {
    // Count favorites per category
    const favoritesPerCategory: { [categoryName: string]: number } = {};
    
    this.userFavoriteApps.forEach(app => {
      const category = app.category || app.categoryName;
      if (category) {
        favoritesPerCategory[category] = (favoritesPerCategory[category] || 0) + 1;
      }
    });
    
    // Update category favorite counts
    this.categories.forEach(category => {
      category.favoriteCount = favoritesPerCategory[category.name] || 0;
    });
  }

  private loadFavoriteCounts(): void {
    // Subscribe to favorite apps changes to update counts
    this.userFavAppService.userFavApps$
      .pipe(takeUntil(this.destroy$))
      .subscribe(favoriteApps => {
        // Update favorite counts for each category
        this.categories.forEach(category => {
          const favoritesInCategory = favoriteApps.filter(app => 
            app.category === category.name || app.category === category.categoryId
          );
          category.favoriteCount = favoritesInCategory.length;
        });
      });
    
    // Load favorites initially
    this.userFavAppService.loadUserFavApps();
  }

  private loadCategories(): void {
    this.isLoading = true;
    this.error = null;

    this.categoryService.getCategoriesWithApps()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response && response.items) {
            this.categories = response.items.sort((a, b) => a.order - b.order);
          } else if (response && response.Items) {
            // Handle different response format
            this.categories = response.Items.sort((a, b) => a.order - b.order);
          } else {
            this.categories = [];
          }
          
          // Update counts immediately with any already-loaded favorites
          this.updateCategoryFavoriteCounts();
          this.isLoading = false;
          
          // After categories load, check current route and set active category if needed
          this.setActiveStateFromRoute(this.router.url);
          
          console.log('Categories loaded:', this.categories.length, 'Favorites:', this.userFavoriteApps.length);
        },
        error: (error) => {
          console.error('Error loading categories:', error);
          this.error = 'Failed to load categories. Please try again later.';
          this.isLoading = false;
          this.categories = [];
        }
      });
  }

  onCategoryClick(category: CategoryWithApps): void {
    // If user is not logged in, redirect to login
    if (!this.isLoggedIn) {
      this.router.navigate(['/login'], { 
        queryParams: { returnUrl: `/category-detail/${category.categoryId}` }
      });
      return;
    }
    
    this.selectedCategory = category;
    this.activeSpecialLink = null;  // Clear special link active state
    this.categorySelected.emit(category);
    
    // Navigate to category detail page with categoryId
    this.router.navigate(['/category-detail', category.categoryId]);
  }

  onFavoritesClick(): void {
    // If user is not logged in, redirect to login
    if (!this.isLoggedIn) {
      this.router.navigate(['/login'], { 
        queryParams: { returnUrl: '/my-favorites' }
      });
      return;
    }
    
    // Set active special link and clear category selection
    this.activeSpecialLink = 'favorites';
    this.selectedCategory = null;
    
    // Navigate to favorites page
    this.router.navigate(['/my-favorites']);
  }

  onCashbackClick(): void {
    // Set active special link and clear category selection
    this.activeSpecialLink = 'cashback';
    this.selectedCategory = null;
    
    // Navigate to cashback request page
    this.router.navigate(['/cashback-request']);
  }

  retryLoading(): void {
    this.loadCategories();
  }

  getIconPath(category: any): string {
    if (category.iconPath && category.iconPath !== '1') {
      return category.iconPath;
    }
    return `/assets/images/default.png`;
  }

  handleImageError(event: any): void {
    event.target.src = '/assets/images/default.png';
    event.target.onerror = null;  // Prevent infinite loop if default image also fails
  }
}
